import React from "react";

/**
 * Reusable "From / To date + Search + Download CSV" control bar for report pages.
 *
 * Usage:
 *   <ReportDateRangeCsv
 *     dateFrom={inputs.date_from}
 *     dateTo={inputs.date_to}
 *     onChange={handleChange}          // same handler you already use for text inputs
 *     onSearch={formSubmit}            // your existing fetchData trigger
 *     csvFilename={`daily-production-report-${inputs.date_from}_to_${inputs.date_to}.csv`}
 *     getCsvData={() => ({
 *       headers: ["S/N", "Name of Products", ...],
 *       rows: receiveData.map((item, i) => {
 *         const v = calculateRowValues(item);
 *         return [i + 1, item.product_name, ...];
 *       }),
 *       // optional trailing row(s), e.g. totals
 *       footerRows: [["", "Total", "", ...]],
 *     })}
 *   />
 *
 * Notes:
 * - `onChange` must update state keys named "date_from" and "date_to"
 *   (it's just your existing handleChange, since it reads e.target.name).
 * - `getCsvData` is called only when the Download button is clicked, so it always
 *   uses the latest rendered data (receiveData/totals) — no stale closures to worry about.
 */

const csvEscape = (value) => {
  const str = value === null || value === undefined ? "" : String(value);
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const rowsToCsv = (headers, rows, footerRows = []) => {
  const lines = [];
  if (headers && headers.length) {
    lines.push(headers.map(csvEscape).join(","));
  }
  rows.forEach((row) => lines.push(row.map(csvEscape).join(",")));
  footerRows.forEach((row) => lines.push(row.map(csvEscape).join(",")));
  // Prepend BOM so Excel opens UTF-8 correctly
  return "\uFEFF" + lines.join("\r\n");
};

export const downloadCsv = (filename, headers, rows, footerRows = []) => {
  const csvContent = rowsToCsv(headers, rows, footerRows);
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename || "report.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const ReportDateRangeCsv = ({
  dateFrom,
  dateTo,
  onChange,
  onSearch,
  csvFilename = "report.csv",
  getCsvData,
  dateFromName = "date_from",
  dateToName = "date_to",
}) => {
  const handleDownload = () => {
    if (!getCsvData) return;
    const { headers = [], rows = [], footerRows = [] } = getCsvData() || {};
    if (!rows.length) {
      window.alert("No data to export.");
      return;
    }
    downloadCsv(csvFilename, headers, rows, footerRows);
  };

  return (
    <div className="row">
      <div className="col-2"></div>
      <div className="col-8">
        <div className="row">
          <div className="col-lg d-flex align-items-center">
            <label className="mg-r-10">From:</label>
            <input
              type="date"
              className="form-control margin-bottom-on-mobile"
              name={dateFromName}
              value={dateFrom || ""}
              onChange={onChange}
              placeholder="From Date"
            />
          </div>
          <div className="col-lg d-flex align-items-center">
            <label className="mg-r-10">To:</label>
            <input
              type="date"
              className="form-control margin-bottom-on-mobile"
              name={dateToName}
              value={dateTo || ""}
              onChange={onChange}
              placeholder="To Date"
            />
          </div>
          <div className="col-lg">
            <button
              className="btn btn-primary btn-block mg-b-10"
              onClick={onSearch}
              type="button"
            >
              <i className="fa fa-send mg-r-10"></i> Search
            </button>
          </div>
          <div className="col-lg">
            <button
              className="btn btn-success btn-block mg-b-10"
              onClick={handleDownload}
              type="button"
            >
              <i className="fa fa-download mg-r-10"></i> Download CSV
            </button>
          </div>
        </div>
      </div>
      <div className="col-2"></div>
    </div>
  );
};

export default ReportDateRangeCsv;